import { useEffect } from 'react';
import { awaitCelebrationIdle } from '@utils/celebrationMarker';
import type { UseWebPushPermissionPromptReturn } from '@hooks/useWebPushPermissionPrompt';

/** Delay before the push permission drawer is offered to an eligible user. */
export const PROMPT_DELAY_MS = 10_000;

/**
 * The slice of `useWebPushPermissionPrompt`'s return the choreography needs.
 * Narrowed with Pick (same pattern as useOneSignalPush's adapter interface) so
 * the hook's contract states exactly what it reads.
 */
type PushPromptChoreographySource = Pick<UseWebPushPermissionPromptReturn, 'shouldPrompt' | 'show'>;

/**
 * Shared opt-in choreography for the push permission drawer: offer the prompt
 * to eligible users after a delay, once no celebration is pending, queued or on
 * screen. Turn-taking with every other overlay (the install drawer included) is
 * the overlay mutex's job: `show()` queues FIFO and the drawer opens when its
 * grant comes up (overlay-system-design §6.6).
 *
 * Waiting for the celebration rather than only queueing behind it matters when two
 * celebrations land back to back: a prompt can't reach its `shouldPrompt` decision
 * before CelebrationHost claims a posted intent, so this isn't about cold start —
 * it's that a bare `show()` could otherwise slip in between the first celebration
 * closing and the second intent being claimed, landing ahead of it.
 *
 * Both push initializers (legacy VAPID and OneSignal owner) must present the
 * IDENTICAL choreography; this hook is the single home for it. Transport
 * differences live entirely in the opt-in adapter each initializer feeds to
 * `useWebPushPermissionPrompt`; the choreography is transport-agnostic.
 */
export function usePushPromptChoreography(
  prompt: PushPromptChoreographySource,
  isAuthenticated: boolean,
): void {
  useEffect(() => {
    if (!prompt.shouldPrompt || !isAuthenticated) return;
    // Checked again after the await below: `awaitCelebrationIdle` can resolve inside a
    // busy-change listener that fires synchronously, so its `.then` reaction can still run a
    // microtask after this effect's cleanup has already fired (a celebration closing and
    // `isAuthenticated`/`shouldPrompt` flipping in the same task). Aborting on teardown means a
    // torn-down effect's `show` call is inert; without the second check here a stale closure
    // could still fire it once the celebration clears.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void awaitCelebrationIdle(controller.signal).then(idle => {
        if (idle && !controller.signal.aborted) prompt.show();
      });
    }, PROMPT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.shouldPrompt, isAuthenticated]);
}
